import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	FormattedText,
	If,
	makeClassName,
	Modal,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { Badge } from "../../../../Components/Badge";
import { Icon } from "../../../../Components/Icon";
import {
	MediaLoadingPlaceholder,
	TextBlockLoadingPlaceholder,
	TextLineLoadingPlaceholder,
} from "../../../../Components/LoadingPlaceholder";
import {
	CalendarEvent,
	SharingPolicy,
	useCalendarEventUserListQuery,
	User,
	UserNameAndImageFragment,
} from "../../../../Utilities";
import { CalendarEventFormat } from "../../../Format";
import { CalendarEventUtilities } from "../../../Utilities";
import * as ModelComponents from "../../_General";
import { AuthorTimestampView } from "../../_General/Author";
import { AttendeesRow, PublicBadge } from "../General";
import { ControlledCalendarEventModal } from "../Modal";
import styles from "./styles.module.css";

export type CalendarEventInTimelineItem = Pick<
	CalendarEvent,
	| "id"
	| "name"
	| "description"
	| "location"
	| "isAllDay"
	| "startTime"
	| "endTime"
	| "startDate"
	| "endDate"
	| "allowsResponses"
> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
	sharingPolicy: Pick<SharingPolicy, "id" | "isPublic"> | null;
};

export type TimelineItemProps = {
	calendarEvent: CalendarEventInTimelineItem | null;
	isLoading?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function TimelineItem({ calendarEvent, isLoading, className, style }: TimelineItemProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	if (!calendarEvent && isLoading) {
		return (
			<div className={makeClassName(styles["timeline-item"], className)} style={style}>
				<Column justify="spaced-start">
					<ModelComponents.AuthorTimestampViewLoadingPlaceholder withTimestamp />
					<div className={styles["timeline-tile--loading__content"]}>
						<Column justify="spaced-start" verticalSpacing="1rem">
							<Column justify="spaced-start" verticalSpacing="0.375rem">
								<Row
									justify="spaced-start"
									align="center"
									className={styles["timeline-item__detail--main"] + " " + styles["timeline-item__detail--name"]}
								>
									<MediaLoadingPlaceholder
										style={{ width: "var(--icon---size)", height: "var(--icon---size)", borderRadius: "33%" }}
									/>
									<TextLineLoadingPlaceholder>Example Event Name</TextLineLoadingPlaceholder>
								</Row>

								<div className={styles["timeline-item__detail__details"]}>
									<TextBlockLoadingPlaceholder lines={2} />
								</div>
							</Column>

							<Column justify="spaced-start" verticalSpacing="0.375rem">
								<Row justify="spaced-start" align="center" className={styles["timeline-item__detail--main"]}>
									<MediaLoadingPlaceholder
										style={{ width: "var(--icon---size)", height: "var(--icon---size)", borderRadius: "33%" }}
									/>
									<TextLineLoadingPlaceholder>Weekday, Month 00</TextLineLoadingPlaceholder>
								</Row>
							</Column>

							<Column justify="spaced-start" verticalSpacing="0.375rem">
								<Row justify="spaced-start" align="center" className={styles["timeline-item__detail--main"]}>
									<MediaLoadingPlaceholder
										style={{ width: "var(--icon---size)", height: "var(--icon---size)", borderRadius: "33%" }}
									/>
									<TextLineLoadingPlaceholder>00 Invited</TextLineLoadingPlaceholder>
								</Row>
							</Column>
						</Column>
					</div>
					<Row justify="flex-end">
						<TextLineLoadingPlaceholder style={{ marginLeft: "0.5rem" }}>View</TextLineLoadingPlaceholder>
					</Row>
				</Column>
			</div>
		);
	}
	if (!calendarEvent) {
		return null;
	}

	return (
		<Column
			justify="spaced-start"
			verticalSpacing="1rem"
			className={makeClassName(styles["timeline-item"], className)}
			style={style}
		>
			<Row justify="spaced-start" style={{ width: "100%", overflow: "hidden", marginRight: "1rem" }}>
				<AuthorTimestampView
					author={calendarEvent.author}
					timestamp={CalendarEventUtilities.startTimeDate(calendarEvent)}
					style={{ width: "fit-content", maxWidth: "100%", overflow: "hidden" }}
				/>
				<Badge color="#fff" backgroundColor="var(--primary-color)" size="small" style={{ height: "fit-content" }}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<Icon.CalendarBlank size={12} /> Event
					</Row>
				</Badge>
			</Row>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<div onClick={openModal} className={styles["timeline-item__content"]}>
					<Column justify="spaced-start" verticalSpacing="1rem">
						<Column justify="spaced-start" verticalSpacing="0.375rem">
							<Row justify="space-between" align="center">
								<Row
									justify="spaced-start"
									align="center"
									className={styles["timeline-item__detail--main"] + " " + styles["timeline-item__detail--name"]}
								>
									<Icon.CalendarBlank /> {calendarEvent.name}
								</Row>
								<If condition={calendarEvent.sharingPolicy?.isPublic ?? false}>
									<PublicBadge size="small" />
								</If>
							</Row>
							<If condition={calendarEvent.description !== ""}>
								<div className={styles["timeline-item__detail__details"]}>
									<TruncatedText lines={2}>
										<FormattedText>{calendarEvent.description}</FormattedText>
									</TruncatedText>
								</div>
							</If>
						</Column>

						<Row justify="spaced-start" align="center" className={styles["timeline-item__detail--main"]}>
							<Icon.Clock /> {CalendarEventFormat.timeRange(calendarEvent)}
						</Row>

						<If condition={calendarEvent.location !== ""}>
							<Row justify="spaced-start" align="center" className={styles["timeline-item__detail--main"]}>
								<Icon.MapPin /> {calendarEvent.location}
							</Row>
						</If>

						<AttendeesSection calendarEventID={calendarEvent.id} />
					</Column>
				</div>

				<Row justify="flex-end">
					<Button variant="link" size="small" onClick={openModal}>
						View
					</Button>
				</Row>
			</Column>

			<Modal.If condition={isModalOpen}>
				<ControlledCalendarEventModal calendarEventID={calendarEvent.id} onClose={closeModal} />
			</Modal.If>
		</Column>
	);
}

type AttendeesSectionProps = {
	calendarEventID: CalendarEvent["id"];
};

function AttendeesSection({ calendarEventID }: AttendeesSectionProps) {
	const { data } = useCalendarEventUserListQuery({
		variables: {
			calendarEventID,
			pagination: { first: 10 },
		},
	});
	const totalUsers = data?.userConnection.totalCount ?? 0;
	const users = React.useMemo(() => data?.userConnection.edges.map((edge) => edge?.node) ?? [], [data]);

	if (totalUsers === 0) {
		return null;
	}

	return (
		<Column justify="spaced-start" verticalSpacing="0.375rem">
			<Row justify="spaced-start" align="center" className={styles["timeline-item__detail--main"]}>
				<Icon.Users /> {totalUsers} Invited
			</Row>
			<div className={styles["timeline-item__detail__details"]}>
				<AttendeesRow
					users={users}
					numAdditional={totalUsers - users.length}
					maxDisplayed={10}
					horizontalSpacing={4}
					iconSize={20}
				/>
			</div>
		</Column>
	);
}
